import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Crown, Library, Skull, Sparkles } from 'lucide-react';
import CardImage from '../../../ui/CardImage';
import Badge from '../../../ui/Badge';
import { cardImageProps } from '../utils/cardView';
import { mergeHandlers } from '../utils/eventHandlers';
import { dragData, dragId } from '../hooks/usePlaytestDnd';
import './ZoneTile.css';

/** Beschriftung und Symbol je Zone; die Teilnehmer-Köpfe zeigen dieselben Symbole. */
export const ZONES = {
  deck: { label: 'Bibliothek', Icon: Library },
  graveyard: { label: 'Friedhof', Icon: Skull },
  exile: { label: 'Exil', Icon: Sparkles },
  commandZone: { label: 'Commander-Zone', Icon: Crown },
};

/**
 * Zonen-Kachel (Spec 2026-10-21 §5.6, §14): Bibliothek, Friedhof, Exil oder Commander-Zone —
 * eine schmale Beschriftungszeile und darunter ein Kartenbild, so groß wie eine Handkarte.
 *
 *   <ZoneTile zone="graveyard" count={n} topCard={top} isOwn
 *     dropId={DROP_IDS.graveyard} onOpenViewer={…} />
 *   <ZoneTile zone="deck" count={n} topCard={openPlay ? top : null} isOwn
 *     dropId={{ top: DROP_IDS.deckTop, bottom: DROP_IDS.deckBottom }} onClick={draw}
 *     contextMenu={(tile) => <ActionContextMenu actions={deckActions(ctx)}>{tile}</ActionContextMenu>} />
 *
 * - Bild: `topCard` über `CardImage` (Seite aus dem Server-Zustand); `onTopCardFlip` (Funktion
 *   oder `undefined`) setzt den Dreh-Knopf: er erscheint nur, solange die Großansicht der
 *   Oberkarte steht, und dreht nur diese Ansicht; Klick und Tastatur bleiben am Knopf und
 *   erreichen die Kachel nicht. Die Bibliothek ohne `topCard` zeigt `CardImage.Back` — die Seite
 *   übergibt `topCard` dort nur bei offen gespielter Bibliothek. Ohne `topCard` zeigen die
 *   übrigen Zonen eine leere Fläche.
 * - Beschriftungszeile über dem Bild: Zonen-Symbol (oder `symbol`) und Zähler (`Badge`, Optik
 *   des Handfächer-Zählers); auf dem Bild selbst liegt nichts. `count` fehlt → `cards.length`.
 * - `symbol`: ersetzt das Zonen-Symbol, etwa durch ein eigenes Bedienelement (die Krone der
 *   Commander-Zone mit dem Steuer-Menü, `CommanderTaxMenu`). Ein Bedienelement dort muss Klick,
 *   Taste und Zeiger-Druck selbst anhalten, sonst löst es zugleich die Kachel aus.
 * - Breite: die CSS-Variable `--zone-tile-w` des Containers (das Dock setzt dort die
 *   Handkarten-Breite); die Bildhöhe ist Breite × 1,4.
 * - Bedienung: Klick auf die Kachel → `onClick`, ohne `onClick` → `onOpenViewer`; ohne beides ist
 *   sie nicht bedienbar. Die Kachel selbst ist eine Gruppe (`role="group"`); Knopf-Rolle,
 *   Tab-Halt und Enter/Leertaste trägt das Kartenbild, damit ein Bedienelement in der
 *   Beschriftungszeile nicht in einem Knopf steckt.
 * - `contextMenu`: Render-Funktion `(tile) => ReactNode`, die die Kachel umschließt (Radix-
 *   `ContextMenu.Trigger asChild`); die Kachel reicht Ref und Handler durch.
 * - Ablageziel: `dropId` als Zeichenkette → das ganze Bild; als `{ top, bottom }` → obere und
 *   untere Bildhälfte getrennt (Bibliothek oben/unten). Ohne `dropId` kein Ablageziel (Gegner).
 *   Das Ziel unter dem Zeiger trägt `data-over`.
 * - `hidden` → nichts gerendert (Exil leer, und es wird gerade keine Karte gezogen).
 * - `previewHandlers`: Zeiger-Handler der schwebenden Vorschau für die sichtbare Oberkarte
 *   (`useHoverPreview.bind(topCard)`); sie laufen zusätzlich zu den Handlern eines Kontextmenüs.
 * - Zieh-Quelle: die Oberkarte (nicht die ganze Kachel, die Ablageziel bleibt) trägt
 *   `useDraggable({ id: dragId(topCard.instanceId), data: dragData(topCard, zone) })`, wenn
 *   `draggableTop` gilt (Standard `isOwn`; ohne `topCard` nie). Ein Klick bleibt Klick, weil
 *   der Maus-Sensor erst ab 5 px startet und dnd-kit den Klick nach einem Zug abfängt. Ohne
 *   `draggableTop` wird `useDraggable` gar nicht aufgerufen: dnd-kit führt Zieh-Quellen nach
 *   ID, und eine zweite Quelle derselben Karte (Zonen-Viewer) löschte beim Abbau den Eintrag
 *   der anderen — die Seite schaltet die Oberkarte deshalb ab, solange ein Viewer offen ist.
 * - Prüfanker: `data-testid="zone-tile"` mit `data-zone`, `data-count`, `data-own`;
 *   `data-testid="zone-tile-label"` (Beschriftungszeile), `data-testid="zone-tile-count"`,
 *   `data-testid="zone-tile-top-card"` mit `data-instance-id` (Oberkarte, ziehbar mit
 *   `aria-roledescription`).
 */
export default function ZoneTile({
  zone, cards, count, topCard, isOwn, dropId, hidden, onClick, onOpenViewer, contextMenu, symbol, previewHandlers, onTopCardFlip,
  draggableTop = isOwn,
}) {
  if (hidden) return null;
  const { label, Icon } = ZONES[zone];
  const total = count ?? cards?.length ?? 0;
  const activate = onClick || onOpenViewer;

  const tile = (
    <ZoneTileBody
      zone={zone}
      label={label}
      Icon={Icon}
      total={total}
      topCard={topCard}
      isOwn={isOwn}
      dropId={dropId}
      activate={activate}
      symbol={symbol}
      previewHandlers={previewHandlers}
      onTopCardFlip={onTopCardFlip}
      draggableTop={!!draggableTop && !!topCard}
    />
  );
  return contextMenu ? contextMenu(tile) : tile;
}

/**
 * Die eigentliche Kachel. Eigene Komponente, damit Radix' `Trigger asChild` seine Props und
 * seine Ref auf das Wurzel-Element legen kann.
 */
const ZoneTileBody = React.forwardRef(function ZoneTileBody(
  { zone, label, Icon, total, topCard, isOwn, dropId, activate, symbol, previewHandlers, onTopCardFlip, draggableTop, ...injected },
  ref,
) {
  const handleKeyDown = (e) => {
    if (e.defaultPrevented || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  const handleClick = (e) => {
    injected.onClick?.(e);
    activate?.();
  };

  return (
    <div
      {...mergeHandlers(injected, previewHandlers)}
      ref={ref}
      className={`zone-tile${activate ? ' zone-tile--active' : ''}`}
      data-testid="zone-tile"
      data-zone={zone}
      data-count={total}
      data-own={isOwn ? 'true' : undefined}
      role="group"
      aria-label={activate ? label : `${label}: ${total}`}
      onClick={handleClick}
    >
      <div className="zone-tile__label" data-testid="zone-tile-label">
        {symbol || <Icon size={14} className="zone-tile__symbol" aria-hidden />}
        <Badge tone="default" size="md" data-testid="zone-tile-count">{total}</Badge>
      </div>
      <div
        className="zone-tile__card"
        role={activate ? 'button' : undefined}
        tabIndex={activate ? 0 : undefined}
        aria-label={activate ? `${label}: ${total}` : undefined}
        onKeyDown={activate ? handleKeyDown : undefined}
      >
        {draggableTop
          ? <DraggableTopCard zone={zone} topCard={topCard} onTopCardFlip={onTopCardFlip} />
          : <ZoneFace zone={zone} topCard={topCard} onTopCardFlip={onTopCardFlip} />}
        {typeof dropId === 'string' && <DropArea id={dropId} part="full" />}
        {dropId && typeof dropId === 'object' && (
          <>
            <DropArea id={dropId.top} part="top" />
            <DropArea id={dropId.bottom} part="bottom" />
          </>
        )}
      </div>
    </div>
  );
});

function ZoneFace({ zone, topCard, onTopCardFlip }) {
  if (topCard) {
    return (
      <div className="zone-tile__top" data-testid="zone-tile-top-card" data-instance-id={topCard.instanceId}>
        <TopCardImage card={topCard} onFlip={onTopCardFlip} />
      </div>
    );
  }
  if (zone === 'deck') return <CardImage.Back />;
  return <div className="zone-tile__empty" />;
}

/**
 * Die Oberkarte als Zieh-Quelle. Von dnd-kits Attributen nur die Beschreibung: der Wrapper trägt
 * weder `role` noch `tabIndex`, Rolle und Tab-Halt trägt das Kartenbild der Kachel; der Dreh-Knopf
 * im Bild ist davon unberührt.
 */
function DraggableTopCard({ zone, topCard, onTopCardFlip }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(topCard.instanceId),
    data: dragData(topCard, zone),
  });
  return (
    <div
      ref={setNodeRef}
      className={`zone-tile__top zone-tile__top--draggable${isDragging ? ' zone-tile__top--dragging' : ''}`}
      data-testid="zone-tile-top-card"
      data-instance-id={topCard.instanceId}
      aria-roledescription={attributes['aria-roledescription']}
      aria-describedby={attributes['aria-describedby']}
      {...listeners}
    >
      <TopCardImage card={topCard} onFlip={onTopCardFlip} />
    </div>
  );
}

function TopCardImage({ card, onFlip }) {
  const { src, backSrc, face, alt } = cardImageProps(card);
  return <CardImage src={src} backSrc={backSrc} face={face} alt={alt} onFlip={onFlip} />;
}

/** Unsichtbare Ablagefläche über dem Bild; Zeiger-Ereignisse gehen an die Kachel durch. */
function DropArea({ id, part }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <span
      ref={setNodeRef}
      className={`zone-tile__drop zone-tile__drop--${part}`}
      data-drop-id={id}
      data-over={isOver ? 'true' : undefined}
      aria-hidden
    />
  );
}
